import blogs from "../blogs.js";
import { DEFAULT_OG_IMAGE, DEFAULT_PAGE_SEO } from "../utils/siteSeo";
import { CLASSROOM_TOOL_PAGES } from "../views/classroom-tools/classroomToolPagesContent";

const SITE_NAME = "Lesson Launchpad";
const SITE_NAME_MARKED = "Lesson Launchpad™";

function pageTitle(title) {
  if (!title || title.includes(SITE_NAME)) {
    return title || DEFAULT_PAGE_SEO.title;
  }

  return `${title} | ${SITE_NAME_MARKED}`;
}

const CLASSROOM_TOOL_PAGE_SEO = Object.fromEntries(
  Object.entries(CLASSROOM_TOOL_PAGES).map(([path, page]) => [
    path,
    {
      title: page.metaTitle,
      description: page.metaDescription,
      path,
      ogType: "article",
    },
  ])
);

const PUBLIC_PAGE_SEO = {
  "/": {
    title: "Lesson Launchpad™ - Automate Classroom Routines",
    description:
      "Automate timers, agendas, reminders, transitions, and music so teachers run class from bell to bell with less manual work.",
    path: "/",
  },
  "/pricing": {
    title: pageTitle("Pricing"),
    description:
      "Simple, affordable pricing for teachers. Start your free trial and automate timers, agendas, reminders, and daily classroom routines with Lesson Launchpad.",
    path: "/pricing",
  },
  "/show-pricing": {
    title: pageTitle("Pricing"),
    description:
      "Simple, affordable pricing for teachers. Start your free trial and automate timers, agendas, reminders, and daily classroom routines with Lesson Launchpad.",
    path: "/pricing",
  },
  "/show": {
    title: pageTitle("Pricing"),
    description:
      "Simple, affordable pricing for teachers. Start your free trial and automate timers, agendas, reminders, and daily classroom routines with Lesson Launchpad.",
    path: "/pricing",
  },
  "/why-lesson-launchpad": {
    title: pageTitle("Why Lesson Launchpad™?"),
    description:
      "Why choose Lesson Launchpad instead of slides, timers, and browser tabs? See how schedule-driven classroom routines reduce screen management while you teach.",
    path: "/why-lesson-launchpad",
  },
  "/whats-new": {
    title: pageTitle("What's New for Existing Users"),
    description:
      "See what changed in Lesson Launchpad for existing users, including schedules, automations, Teacher Hub, Class Notes, and export workflows.",
    path: "/whats-new",
    noIndex: true,
  },
  "/active-dates-vs-linked-boxes": {
    title: pageTitle("Active Dates vs Linked Boxes"),
    description:
      "Visual guide to reusing Content Manager boxes across active dates, linked classes, or both in Lesson Launchpad.",
    path: "/active-dates-vs-linked-boxes",
    noIndex: true,
  },
  "/blog": {
    title: pageTitle("Blog"),
    description:
      "Tips, guides, and product updates for teachers using Lesson Launchpad to automate classroom routines.",
    path: "/blog",
  },
  "/contact": {
    title: pageTitle("Contact"),
    description:
      "Contact Lesson Launchpad with questions, feedback, or school licensing inquiries. We are happy to help teachers get started.",
    path: "/contact",
  },
  "/privacy": {
    title: pageTitle("Privacy Policy"),
    description: "Read the Lesson Launchpad privacy policy.",
    path: "/privacy",
  },
  "/trust": {
    title: pageTitle("Trust Center"),
    description: "Security, privacy, compliance, and data practices for Lesson Launchpad.",
    path: "/trust",
  },
  "/for-administrators": {
    title: pageTitle("For Schools & Administrators"),
    description:
      "Improve classroom consistency and save instructional time with Lesson Launchpad. Designed for schools and districts.",
    path: "/for-administrators",
  },
  "/ambassadors": {
    title: pageTitle("Ambassador Program"),
    description:
      "Join the Lesson Launchpad Ambassador Program and help educators discover better ways to automate classroom routines through mentorship, professional development, content, feedback, and leadership.",
    path: "/ambassadors",
    ogTitle: "Become a Lesson Launchpad™ Ambassador",
    ogDescription:
      "Support teachers, lead professional learning, and help shape the future of classroom automation.",
  },
  "/ambassador-program": {
    title: pageTitle("Ambassador Program"),
    description:
      "Join the Lesson Launchpad Ambassador Program and help educators discover better ways to automate classroom routines through mentorship, professional development, content, feedback, and leadership.",
    path: "/ambassadors",
    ogTitle: "Become a Lesson Launchpad™ Ambassador",
    ogDescription:
      "Support teachers, lead professional learning, and help shape the future of classroom automation.",
  },
  "/purchase-order": {
    title: pageTitle("Purchase Order & Quote Request"),
    description:
      "Request a purchase order quote and W-9 for Lesson Launchpad school or district licensing. Self-serve documents for business office approval.",
    path: "/purchase-order",
  },
  "/auth/register": {
    title: pageTitle("Create Your Account"),
    description:
      "Create your Lesson Launchpad account and start automating timers, agendas, reminders, and classroom routines.",
    path: "/auth/register",
    noIndex: true,
  },
  "/auth/login": {
    title: pageTitle("Log In"),
    description: "Log in to your Lesson Launchpad account.",
    path: "/auth/login",
    noIndex: true,
  },
  "/auth/logout": {
    title: pageTitle("Log Out"),
    description: "Log out of your Lesson Launchpad account.",
    path: "/auth/logout",
    noIndex: true,
  },
  "/account/subscription": {
    title: pageTitle("Subscription"),
    description: "Manage your Lesson Launchpad subscription.",
    path: "/account/subscription",
    noIndex: true,
  },
  "/music-tools": {
    title: pageTitle("Free Music Tools for Teachers"),
    description:
      "Free online metronome, tuner, and tone generator tools for music teachers and classrooms.",
    path: "/music-tools",
  },
  "/metronome": {
    title: pageTitle("Free Online Metronome"),
    description:
      "Use this free online metronome in your classroom or rehearsal. Adjust tempo, volume, and subdivisions.",
    path: "/metronome",
  },
  "/tuner": {
    title: pageTitle("Free Online Tuner"),
    description: "Tune instruments in class with this free online tuner from Lesson Launchpad.",
    path: "/tuner",
  },
  "/tone-generator": {
    title: pageTitle("Free Online Tone Generator"),
    description:
      "Generate reference pitches for ear training, tuning, and classroom music activities.",
    path: "/tone-generator",
  },
  "/out-of-tune": {
    title: pageTitle("Out of Tune Ear Training Game"),
    description:
      "Practice identifying out-of-tune notes with this free ear-training tool for music classrooms.",
    path: "/out-of-tune",
  },
  "/embed/music-tools": {
    title: pageTitle("Music Tools Embed"),
    description: "Embeddable music tools for classrooms.",
    path: "/embed/music-tools",
    noIndex: true,
  },
  "/embed/metronome": {
    title: pageTitle("Metronome Embed"),
    description: "Embeddable metronome for classrooms.",
    path: "/embed/metronome",
    noIndex: true,
  },
  "/embed/tone-generator": {
    title: pageTitle("Tone Generator Embed"),
    description: "Embeddable tone generator for classrooms.",
    path: "/embed/tone-generator",
    noIndex: true,
  },
  "/embed/tuner": {
    title: pageTitle("Tuner Embed"),
    description: "Embeddable tuner for classrooms.",
    path: "/embed/tuner",
    noIndex: true,
  },
  "/tools": {
    title: pageTitle("Free Teacher Tools"),
    description:
      "Free classroom tools for teachers: timers, random student picker, metronome, tuner, and more.",
    path: "/tools",
  },
  "/classroom-timer": {
    title: pageTitle("Free Classroom Timer for Teachers"),
    description:
      "Display a free classroom timer on your projector or smartboard with presets for transitions, work time, stations, and daily routines.",
    path: "/classroom-timer",
  },
  "/countdown-timer": {
    title: pageTitle("Free Countdown Timer for Class"),
    description:
      "Use a quick classroom countdown timer for brain breaks, tests, cleanup, and transitions. Works on Smartboards, projectors, and classroom displays.",
    path: "/countdown-timer",
  },
  "/random-student-picker": {
    title: pageTitle("Random Student Picker for Teachers"),
    description:
      "Pick a random student fairly during class discussions. Paste your roster, prevent repeats, and keep participation simple for every lesson.",
    path: "/random-student-picker",
  },
  "/classroomscreen-alternative": {
    title: pageTitle("ClassroomScreen Alternative for Teachers"),
    description:
      "Compare ClassroomScreen with Lesson Launchpad for teachers who need automated timers, agendas, reminders, music, and class transitions.",
    path: "/classroomscreen-alternative",
  },
  "/compare/classroomscreen-vs-lesson-launchpad": {
    title: "ClassroomScreen vs Lesson Launchpad™ for Teachers",
    description:
      "Compare ClassroomScreen and Lesson Launchpad for classroom timers, agendas, music automation, reminders, and daily teacher routines.",
    path: "/compare/classroomscreen-vs-lesson-launchpad",
  },
  "/compare/google-slides-classroom-timer": {
    title: pageTitle("Google Slides Classroom Timer Alternative"),
    description:
      "Replace manual Google Slides timers with automated classroom countdowns, routines, and reminders on one teacher-friendly present screen.",
    path: "/compare/google-slides-classroom-timer",
  },
  "/compare/smartboard-classroom-tools": {
    title: pageTitle("Smartboard Classroom Tools for Teachers"),
    description:
      "Find classroom tools for Smartboards, Promethean boards, and projectors, including timers, random picker tools, music tools, and routines.",
    path: "/compare/smartboard-classroom-tools",
  },
  "/what-is-classroom-automation": {
    title: pageTitle("What Is Classroom Automation for Teachers?"),
    description:
      "Learn how classroom automation helps teachers run routines, timers, transitions, reminders, and classroom management tasks with less manual work.",
    path: "/what-is-classroom-automation",
    ogType: "article",
  },
  ...CLASSROOM_TOOL_PAGE_SEO,
  "/debug": {
    title: pageTitle("Debug"),
    description: "Internal debug page.",
    path: "/debug",
    noIndex: true,
  },
};

function getBlogPostSeo(blogId) {
  const blogPost = blogs.find((blog) => blog.id === blogId || blog.url === blogId);
  if (!blogPost) {
    return null;
  }

  return {
    title: pageTitle(blogPost.metaTitle || blogPost.title),
    description: blogPost.metaDescription || blogPost.description,
    path: `/blog/${blogPost.url || blogPost.id}`,
    image: blogPost.cardImage || DEFAULT_OG_IMAGE,
    ogType: "article",
    noIndex: blogPost.show !== true,
  };
}

export function getSeoForPath(pathname) {
  if (PUBLIC_PAGE_SEO[pathname]) {
    return PUBLIC_PAGE_SEO[pathname];
  }

  const blogMatch = pathname.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) {
    return getBlogPostSeo(blogMatch[1]);
  }

  const schoolMatch = pathname.match(/^\/school\/([^/]+)$/);
  if (schoolMatch) {
    return {
      title: pageTitle("School Registration"),
      description:
        "Create your Lesson Launchpad account through your school's registration link.",
      path: pathname,
      noIndex: true,
    };
  }

  if (pathname === "/share" || pathname.startsWith("/share/")) {
    return {
      title: pageTitle("Shared Class"),
      description: "View shared Lesson Launchpad classroom content.",
      path: pathname,
      noIndex: true,
    };
  }

  return null;
}
